import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import { SelectorConfig } from '../../../models/ui-config.models';

@Component({
  selector: 'app-calls-filter',
  templateUrl: './calls-filter.component.html',
  styleUrls: ['./calls-filter.component.scss'],
})

export class CallsFilterComponent implements OnInit, OnChanges {
  @Input() configs: SelectorConfig[] = [];
  @Output() valueChange = new EventEmitter<{ key: string; values: Record<string, any> }>();

  public values: Record<string, any> = {};

  ngOnInit(): void {
    this.initValuesFromConfigs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.configs) {
      this.updateValuesFromChangedConfigs(
        changes.configs.previousValue || [],
        changes.configs.currentValue || []
      );
    }
  }

  protected updateValuesFromChangedConfigs(
    previous: SelectorConfig[],
    current: SelectorConfig[]
  ): void {
    const updatedKeys = this.getChangedConfigKeys(previous, current);

    current.forEach(config => {
      if (updatedKeys.has(config.key)) {
        this.values[config.key] = this.getDefaultValue(config);
      }
    });
  }

  protected getChangedConfigKeys(
    previous: SelectorConfig[],
    current: SelectorConfig[]
  ): Set<string> {
    const updatedKeys = new Set<string>();

    current.forEach(curr => {
      const prev = previous.find(p => p.key === curr.key);
      const hasChanged = !prev || JSON.stringify(prev) !== JSON.stringify(curr);

      if (hasChanged) {
        updatedKeys.add(curr.key);
      }
    });

    return updatedKeys;
  }

  protected getDefaultValue(config: SelectorConfig): any {
    return config.value ?? (config.type === 'multi' ? [] : null);
  }

  protected initValuesFromConfigs(): void {
    this.values = {};
    this.configs.forEach(config => {
      this.values[config.key] = config.value ?? (config.type === 'multi' ? [] : null);
    });
  }

  protected getDisplayValue(config: SelectorConfig): { label: string; remainingCount: number } {
    const selectedValues = this.values[config.key];

    if (!Array.isArray(selectedValues)) {
      return { label: '', remainingCount: 0 };
    }

    const optionsMap = new Map<any, string>(
      config.options.map(opt => [opt.value, opt.title])
    );

    const firstValue = selectedValues[0];
    const firstLabel = optionsMap.get(firstValue) || firstValue;

    const remaining = selectedValues.length - 1;

    return {
      label: firstLabel,
      remainingCount: remaining > 0 ? remaining : 0
    };
  }

  private normalizeSelection(values: number[], config: SelectorConfig): number[];
  private normalizeSelection(values: string[], config: SelectorConfig): string[];

  private normalizeSelection<T extends number | string>(values: T[], config: SelectorConfig): T[] {
    const totalOptions = config.options.length - 1;
    const baseValue = config.baseValue;
    const selectedCount = values.length;

    const hasAll = typeof baseValue !== 'object' && values.includes(baseValue as T);

    if (!selectedCount || selectedCount === totalOptions) {
      return [baseValue as T];
    }

    if (hasAll && selectedCount > 1) {
      const prevHadAll = Array.isArray(config.prevValue)
      ? config.prevValue.includes(baseValue)
      : false;

      return prevHadAll
        ? values.filter(id => id !== baseValue)
        : [baseValue as T];
    }

    return values;
  }

  protected onChange(configKey: string, value: any): void {
    const config = this.configs.find(c => c.key === configKey);
    if (config.type == "multi") {
      const normalizedValue = this.normalizeSelection(value, config);
      this.values[configKey] = normalizedValue;
      config.prevValue = normalizedValue;
    } else {
      this.values[configKey] = value;
    }
    this.valueChange.emit({ key: configKey, values: { ...this.values } });
  }
}
